/**
 * PulseBotContext.tsx
 *
 * Global open/close state for the PulseBot chat panel (cloned from
 * CommandPaletteContext). Any component can open the bot via
 * `usePulseBot().open()` / `.toggle()`.
 */

import React, { createContext, useCallback, useContext, useState } from 'react';

interface PulseBotContextValue {
  isOpen: boolean;
  open:   () => void;
  close:  () => void;
  toggle: () => void;
}

const PulseBotContext = createContext<PulseBotContextValue | null>(null);

export function PulseBotProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open   = useCallback(() => setIsOpen(true), []);
  const close  = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(v => !v), []);

  return (
    <PulseBotContext.Provider value={{ isOpen, open, close, toggle }}>
      {children}
    </PulseBotContext.Provider>
  );
}

export function usePulseBot() {
  const ctx = useContext(PulseBotContext);
  if (!ctx) throw new Error('usePulseBot must be used inside PulseBotProvider');
  return ctx;
}
